import Glide, {
  Controls,
  Swipe,
  Images,
} from "@glidejs/glide/dist/glide.modular.esm";
import { i18n, initTranslation } from "../modules/i18n.js";
import initPage from "../modules/page";
import { stickyElement, visualSelect } from "../modules/ui.js";
import { sendFacebookPixelEvent } from "../modules/analytics/facebook-pixel";
import {
  sendPinterestPageViewEvent,
  sendPinterestAddToCartEvent,
} from "../modules/analytics/pinterest";
import {
  initCarousels,
  initCarouselGroup,
} from "../modules/product-collection";
import xhr from "../modules/xhr.js";
import initGeoIPModal from "../modules/geoip-modal";
import { slideToggle, slideDown, minWidth } from "../modules/utilities";
import { initAccordion, initToggleClassLinks } from "../modules/ui.js";
import { buyNowRequest } from "../modules/api";
import { Logger } from "@fruugo/utilities";
import { isInABTest } from "../modules/abTesting.js";
import { initKlarnaOnsiteMessaging } from "../modules/klarna-messaging";
import { initPayPalMessaging } from "../modules/paypal-messaging.js";
import "../../scss/product.scss";
import { initProductTranslations } from "../modules/product-translations";
import { ReadMoreList } from "../modules/read-more-list";
import { FruugoAnalyticsProductPageViewEvent } from "../modules/analytics/fruugo-analytics/FruugoAnalyticsProductPageViewEvent";
import { FruugoAnalyticsBuyNowEvent } from "../modules/analytics/fruugo-analytics/FruugoAnalyticsBuyNowEvent";
import { select } from "@fruugo/utilities";
import { handleAddToCart } from "../modules/events/handleAddToCart";
import {
  sendAddToCartEvent,
  sendSelectContentEvent,
  sendViewItemEvent,
} from "../modules/analytics/google-analytics";
import {
  sendRTBProductPageViewEvent,
  sendRTBAddToCartEvent,
} from "../modules/analytics/rtb-house";
import {
  sendTikTokPageViewEvent,
  sendTikTokAddToCartEvent,
} from "../modules/analytics/tiktok";

const Log = new Logger(window.environment);

const {
  retailerInformation,
  skuInfo,
  breadcrumbs,
  productId,
  cheapestShippingMethod,
} = window;

const clamp = (number, min, max) => Math.max(min, Math.min(number, max));
const quantitySelector = document.getElementById("quantity-selector");
const maxQuantity = quantitySelector ? quantitySelector.options.length : 1;
let productPillTimer;

const addButton = document.querySelector(".js-add-quantity-button");
const removeButton = document.querySelector(".js-remove-quantity-button");
const addToCartButton = document.querySelectorAll(".js-add-to-cart");
const buyNowButton = document.querySelectorAll(".js-buy-now");

// Reset quantity selector on page load
// (last selected value can get stuck if user uses "Back" button on browser)
if (quantitySelector) quantitySelector.value = 1;

const updateQuantity = () => {
  if (!quantitySelector) return;

  // Callback
  const update = (e, amount) => {
    e.preventDefault();
    let currentQuantity = parseInt(quantitySelector.value);

    // Update current quantity
    currentQuantity = clamp(currentQuantity + amount, 1, maxQuantity);

    // Update select dropdown value
    quantitySelector.value = currentQuantity;

    // Enable/disable buttons
    addButton.disabled = currentQuantity === maxQuantity;
    removeButton.disabled = currentQuantity === 1;

    // Update shipping cost
    updateShippingCost(currentQuantity);
  };

  // Attach listeners
  addButton.addEventListener("click", (e) => update(e, 1));
  removeButton.addEventListener("click", (e) => update(e, -1));
};

const initUpdateShippingCost = () => {
  quantitySelector &&
    quantitySelector.addEventListener("change", (event) =>
      updateShippingCost(event.target.value)
    );
};

const updateShippingCost = (quantity) => {
  const { skuInfo, currentCountry, currentLang } = window;
  const { id } = skuInfo;
  const shippingCostElement = document.querySelector(".js-shipping-cost");

  const updateBuyButtons = (disabled) => {
    addToCartButton.forEach((button) => (button.disabled = disabled));
    buyNowButton.forEach((button) => (button.disabled = disabled));
  };

  updateBuyButtons(false);

  fetch(
    `/marketplace/api/shipping-cost/${id}?quantity=${quantity}&country=${currentCountry}&language=${currentLang}`
  )
    .then((res) => res.json())
    .then((data) => {
      const { shippingCost, numericShippingCost, hasAvailableShippingMethods } =
        data;

      if (!hasAvailableShippingMethods) {
        // TODO Duplicated in ui.js, extract out to a utility
        const urlLangParam =
          window.currentLang === window.defaultLang
            ? ""
            : "language=" + window.currentLang;

        shippingCostElement.innerHTML = `<span class="text-danger">${i18n(
          "cart.itemnotavailable.notshippable",
          `/help?${urlLangParam}`
        )}</span>`;

        updateBuyButtons(true);

        return;
      }

      if (
        typeof numericShippingCost === "undefined" ||
        numericShippingCost === null
      )
        return;

      if (numericShippingCost > 0) {
        shippingCostElement.innerHTML = `<span class="text-muted text-nowrap">${i18n(
          "product.paid.shipping",
          shippingCost
        )}</span>`;
      } else {
        shippingCostElement.innerHTML = `<strong class="text-success text-nowrap">${i18n(
          "product.free.shipping"
        )}</strong>`;
      }
    })
    .catch((error) => Log.error(error));
};

function productPageAddToCart(e) {
  e.preventDefault();
  const { skuId } = document.getElementById("add-to-cart").dataset;
  const quantity = clamp(quantitySelector.value, 1, maxQuantity);

  handleAddToCart(skuId, quantity);
}

function productPageBuyNow(e) {
  e.preventDefault();

  // disable buttons to avoid double clicks (handles sticky header as well as buybox)
  document.querySelectorAll("button.js-buy-now").forEach((item) => {
    item.disabled = true;
    item.classList.add("btn-loading");
  });

  const { skuId } = document.getElementById("add-to-cart").dataset;
  const quantity = clamp(quantitySelector.value, 1, maxQuantity);

  buyNowRequest(skuId, quantity)
    .then((response) => {
      if (response.cartValid === false || !response.checkoutUrl) {
        throw new Error();
      }

      if (response.itemAddedToCart) {
        sendAddToCartEvent(
          skuInfo,
          quantity,
          {
            buy_now: true,
          },
          {
            sku_id: skuInfo.id,
            retailer_id: retailerInformation.id,
            retailer_name: retailerInformation.name,
            retailer_country: retailerInformation.country,
            days_to_dispatch: skuInfo.shipping.daysToDispatch,
            days_to_deliver: cheapestShippingMethod?.daysToDelivery,
            shipping_cost: cheapestShippingMethod?.numericPrice,
            free_shipping: cheapestShippingMethod?.free,
            item_available: skuInfo.sellable,
          }
        );

        sendFacebookPixelEvent("AddToCart", {
          value:
            skuInfo.price.numericTotalInclTaxAfterDiscount ??
            skuInfo.price.numericTotalInclTax,
          currency: skuInfo.price.currencyCode,
          contents: [{ id: skuInfo.productId, quantity }],
          content_type: "product",
        });

        const event = new FruugoAnalyticsBuyNowEvent(productId);
        event.send();

        sendRTBAddToCartEvent(skuId);

        sendPinterestAddToCartEvent(
          window.skuInfo.productId,
          window.skuInfo.title,
          window.skuInfo.price.numericTotalInclTaxAfterDiscount ??
            window.skuInfo.price.numericTotalInclTax,
          quantity,
          window.skuInfo.price.currencyCode
        );
        sendTikTokAddToCartEvent(
          window.skuInfo.productId,
          window.skuInfo.title,
          window.skuInfo.price.currencyCode,
          window.skuInfo.description,
          quantity,
          window.skuInfo.price.numericTotalInclTaxAfterDiscount ??
            window.skuInfo.price.numericTotalInclTax
        );

        // Slight delay to allow analytics to send
        setTimeout(() => {
          window.location.href = response.checkoutUrl;
        }, 75);
      } else {
        window.location.href = response.checkoutUrl;
      }
    })
    .catch(() => {
      window.location.href = "/cart";
    });
}

function initProductPageAddToCartEvent() {
  if (addToCartButton) {
    addToCartButton.forEach((button) => {
      button.addEventListener("click", (event) => {
        productPageAddToCart(event);
      });
    });
  }

  if (buyNowButton) {
    buyNowButton.forEach((button) => {
      button.addEventListener("click", (event) => {
        productPageBuyNow(event);
      });
    });
  }
}

function productPageDeliveryToggle() {
  const deliveryContainer = document.querySelector(".js-delivery-container");
  const deliveryToggle = document.querySelector(".js-delivery-toggle");
  const deliveryOpen = document.querySelectorAll(".js-delivery-open");

  if (isInABTest("cxo2-224-delivery-info", "C")) {
    slideToggle(deliveryContainer);
  }

  if (deliveryToggle) {
    deliveryToggle.addEventListener("click", (event) => {
      event.preventDefault();
      slideToggle(deliveryContainer);

      setTimeout(() => {
        const deliveryShown =
          deliveryContainer.classList.contains("is-visible");
        sendSelectContentEvent(
          "delivery_returns",
          deliveryShown ? "view" : "hide"
        );
      }, 350);
    });
  }

  if (deliveryOpen) {
    deliveryOpen.forEach((item) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        slideDown(deliveryContainer);

        const action = event.currentTarget.getAttribute(
          "data-analytics-action"
        );
        sendSelectContentEvent("delivery_returns", action);
      });
    });
  }
}

function productPageLayout() {
  // Sticky TOC
  const stickyTrigger = document.querySelector(".js-sticky-trigger");
  const bodyRectTop = document.body.getBoundingClientRect().top;
  if (stickyTrigger) {
    const stickyTriggerRectTop = stickyTrigger.getBoundingClientRect().top;
    minWidth(1280, () => {
      stickyElement(
        ".js-sticky",
        stickyTriggerRectTop - bodyRectTop - 18,
        "d-xl-block"
      );
    });
  }
}

function productPageConfigurable() {
  visualSelect();
}

function initImageGalleryZoom(container) {
  const img = document.querySelector(container).querySelector("img");

  document
    .querySelector(container)
    .addEventListener("mouseover", function (event) {
      const imgWidth = img.naturalWidth;
      const box = event.currentTarget;
      const boxClassName = box.getAttribute("class");
      const boxWidth =
        document.getElementsByClassName(boxClassName)[0].offsetWidth;
      if (boxWidth < imgWidth) {
        const x = imgWidth / boxWidth;
        img.style.transform = "scale(" + x + ")";
      } else {
        img.style.cursor = "default";
      }
    });

  document.querySelector(container).addEventListener("mouseout", function () {
    img.style.transform = "scale(1)";
  });

  document
    .querySelector(container)
    .addEventListener("mousemove", function (event) {
      const imgWidth = img.naturalWidth;
      const box = event.currentTarget;
      const boxClassName = box.getAttribute("class");
      const boxElement = document.getElementsByClassName(boxClassName)[0];
      const boxWidth = boxElement.offsetWidth;
      const boxHeight = boxElement.offsetHeight;
      const boxOffsetLeft = boxElement.getBoundingClientRect().left;
      const boxOffsetTop = boxElement.getBoundingClientRect().top;
      if (boxWidth < imgWidth) {
        img.style.transformOrigin =
          ((event.pageX - boxOffsetLeft) / boxWidth) * 100 +
          "%" +
          ((event.pageY - boxOffsetTop) / boxHeight) * 100 +
          "%";
      }
    });
}

function productPageGallery() {
  const productGalleryContainer = document.querySelector(
    ".ProductGallery--carousel"
  );
  const productGalleryItems = productGalleryContainer.querySelectorAll(
    ".ProductGallery__Item"
  );
  const productGalleryNavigation =
    productGalleryContainer.querySelector(".glide__bullets");
  // Mobile
  minWidth(
    992,
    false,
    () => {
      if (productGalleryItems.length > 1) {
        new Glide(productGalleryContainer, {
          perView: 1,
          animationDuration: 600,
          direction: window.rtl === "true" ? "rtl" : "ltr",
        }).mount({ Controls, Swipe, Images });
        productGalleryNavigation.classList.remove("d-none");
      }
    },
    true
  );

  // Desktop
  minWidth(992, () => {
    const container = ".js-hover-zoom";
    if (document.querySelector(".js-gallery-thumbs")) {
      const siblings = document
        .querySelector(".js-gallery-thumbs")
        .querySelectorAll(".js-gallery-thumb");
      siblings.forEach((item) => {
        const url = item.getAttribute("data-image");
        const thumbnailUrl = url.replace("_max", "_0100_0100");
        const thumbnail = new Image();
        thumbnail.src = thumbnailUrl;
        thumbnail.className = "ProductGallery__Thumb__Image";
        thumbnail.setAttribute("onerror", "handleImageError(this)");
        item.appendChild(thumbnail);

        item.addEventListener("click", function (event) {
          event.currentTarget.classList.add("ProductGallery__Thumb--active");
          siblings.forEach((item) => {
            if (event.currentTarget !== item) {
              item.classList.remove("ProductGallery__Thumb--active");
            }
          });

          const url = event.currentTarget.dataset.image;
          document
            .querySelector(container)
            .querySelector("img")
            .setAttribute("src", url);
        });
      });
    }
    initImageGalleryZoom(container);
  });
}

function initRecommendations() {
  renderCategoryRecommendations();
  renderBrandRecommendations();
}

function renderCategoryRecommendations() {
  const container = document.querySelector(".js-recommended-categories");
  if (container === null) return;
  const { brandName } = container.dataset;

  // undefined brands shouldnt render
  if (
    brandName === undefined ||
    ["no brand", "unbranded", "n/a"].includes(brandName.toLowerCase())
  ) {
    container.style.display = "none";
    return;
  }

  renderRecommendation(
    container,
    `/marketplace/api/recommendations/category?brand=${brandName}&language=${window.currentLang}`
  );
}

function renderBrandRecommendations() {
  const container = document.querySelector(".js-recommended-brands");
  if (container === null) return;
  const { categoryCode } = container.dataset;
  renderRecommendation(
    container,
    `/marketplace/api/recommendations/brand?category=${categoryCode}&language=${window.currentLang}`
  );
}

function renderRecommendation(container, url) {
  container.innerHTML = Loader();

  xhr(url)
    .then((request) => {
      const links = JSON.parse(request.response);
      if (links.recommendedLinks === null) {
        throw new Error("no links");
      }
      container.innerHTML = RecommendationList(links);
    })
    .catch(() => {
      container.innerHTML = "";
    });
}

const Loader = () =>
  `<img src="/marketplace/images/loader.svg" class="loader" style="width:50px; height:auto; display:block; margin:20px auto;" />`;

const RecommendationList = ({ titleLink, seeAllLink, recommendedLinks }) =>
  `<div>
    <h2>${titleLink.text}</h2>
    <ul class="list-unstyled">
        ${recommendedLinks
          .map(
            (item) =>
              `<li><a href="${item.link.href}">${item.link.text} <span>(${item.count})</span></a></li>`
          )
          .join("")}
    </ul>
    <a aria-label="${seeAllLink.text} ${titleLink.text.toLowerCase()}" href="${
    seeAllLink.href
  }">${seeAllLink.text}</a>
</div>
`;

function initPageSpecificAnalytics() {
  sendViewItemEvent(skuInfo, null, {
    sku_id: skuInfo.id,
    retailer_id: retailerInformation.id,
    retailer_name: retailerInformation.name,
    retailer_country: retailerInformation.country,
    days_to_dispatch: skuInfo.shipping.daysToDispatch,
    days_to_deliver: cheapestShippingMethod?.daysToDelivery,
    shipping_cost: cheapestShippingMethod?.numericPrice,
    free_shipping: cheapestShippingMethod?.free,
    item_available: skuInfo.sellable,
  });

  sendFacebookPixelEvent("ViewContent", {
    value:
      skuInfo.price.numericTotalInclTaxAfterDiscount ??
      skuInfo.price.numericTotalInclTax,
    currency: skuInfo.price.currencyCode,
    content_ids: [productId],
    content_type: "product",
    content_category: breadcrumbs
      .map((b) => b.text)
      .reduce((current, prev) => `${current} > ${prev}`),
  });

  const event = new FruugoAnalyticsProductPageViewEvent(skuInfo.productId);
  event.send();

  sendRTBProductPageViewEvent(productId);

  sendPinterestPageViewEvent();
  sendTikTokPageViewEvent(
    skuInfo.productId,
    skuInfo.title,
    skuInfo.price.numericTotalInclTaxAfterDiscount ??
      skuInfo.price.numericTotalInclTax,
    skuInfo.price.currencyCode,
    skuInfo.description
  );
}

function onConsentUpdate() {
  document.addEventListener("consentUpdate", (e) => {
    const event = new FruugoAnalyticsProductPageViewEvent(skuInfo.productId);
    event.send();

    // Wait for abtest & fruugo_client_id cookie to be returned
    setTimeout(() => {
      switchBytePlusCarousels();
    }, 1500);

    const consents = e.detail.consents;
    if (consents && consents.advertising) {
      sendRTBProductPageViewEvent(productId);
    }
  });
}

function switchBytePlusCarousels() {
  if (isInABTest("SHOP-858-BPCarousels", "B")) {
    const bpContainer = select(".js-bp-carousel-container");
    const fruugoContainer = select(".js-fruugo-carousel-container");

    bpContainer.classList.remove("d-none");
    fruugoContainer.remove();

    initCarouselGroup();
    window.dispatchEvent(new Event("resize")); // Force resize to fix carousel layout issues
  }
}

function productImagePill() {
  const pills = document.querySelectorAll(".js-product-gallery-pill");
  const pillCloseClass = "ProductGallery__Pill--close";
  const pillTimeToFade = 6000;

  if (pills) {
    clearTimeout(productPillTimer);
    pills.forEach((pill) => {
      productPillTimer = setTimeout(() => {
        pill.classList.add(pillCloseClass);
      }, pillTimeToFade);

      const pillCloseButton = pill.querySelector(".js-product-pill-close");

      if (pillCloseButton) {
        pillCloseButton.addEventListener("click", () => {
          pill.classList.add(pillCloseClass);
        });
      }

      pill.addEventListener("animationend", (event) => {
        if (event.animationName === "move-down") {
          pill.classList.add("d-none");
        }
      });
    });
  }
}

async function initProductPage() {
  await initTranslation();
  initPage();
  productPageLayout();
  productPageConfigurable();
  productPageGallery();
  productPageDeliveryToggle();
  initProductPageAddToCartEvent();
  initCarousels();
  initProductTranslations();
  initGeoIPModal();
  initRecommendations();
  updateQuantity();
  initPageSpecificAnalytics();
  initAccordion();
  initToggleClassLinks();
  productImagePill();
  initUpdateShippingCost();
  initKlarnaOnsiteMessaging();
  initPayPalMessaging();
  onConsentUpdate();

  if (isInABTest("SHOP-858-BPCarousels", "B")) {
    initCarouselGroup();
  }

  const readMoreList = new ReadMoreList(".js-read-more-list");
  readMoreList.init();
}

initProductPage();
